/**
 * Which rekhā is which
 *
 * Assigns traced polylines to the classical lines using their position and
 * direction in the **palm frame** rather than in the image — so a rotated,
 * enlarged or left-handed photograph is read the same way (see hand-geometry.js;
 * u = 0 at the thumb side, 1 at the ulnar edge, v = 0 at the wrist, 1 at the webs):
 *
 *   Hṛdaya Rekhā   heart    — upper transverse, from the ulnar edge
 *   Mastiṣka Rekhā head     — middle transverse, from the thumb side
 *   Jīvana Rekhā   life     — arc around the Mount of Śukra
 *   Bhāgya Rekhā   fate     — vertical, wrist toward Śani
 *   Sūrya Rekhā    sun      — vertical toward the ring finger
 *   Budha Rekhā    mercury  — vertical/diagonal toward the little finger
 *   Vivāha Rekhā   marriage — short horizontals on the ulnar edge below the little finger
 *
 * ⚠ Each assignment carries a confidence, and every one is user-correctable. A
 * curve that fits nothing well is left **unclassified**; one that fits two types
 * about equally is labelled with a *low* confidence and says which two. The UI
 * must never present a low-confidence trace as fact.
 *
 * The templates are position priors, not a learned model. They encode where the
 * tradition says each line lies; the numbers (centres and spreads) are engineering
 * choices — tunable against real photographs and flagged as such in the plan.
 *
 * © Manish Jagdish Thatte
 */

import { toPalmSpace } from './hand-geometry.js';
import { lengthOf, simplify } from './polyline.js';

export const LINE_TYPES = Object.freeze([
  Object.freeze({ id: 'heart', sanskrit: 'Hṛdaya Rekhā', english: 'Heart line', max: 1 }),
  Object.freeze({ id: 'head', sanskrit: 'Mastiṣka Rekhā', english: 'Head line', max: 1 }),
  Object.freeze({ id: 'life', sanskrit: 'Jīvana Rekhā', english: 'Life line', max: 1 }),
  Object.freeze({ id: 'fate', sanskrit: 'Bhāgya Rekhā', english: 'Fate line', max: 1 }),
  Object.freeze({ id: 'sun', sanskrit: 'Sūrya Rekhā', english: 'Sun line', max: 1 }),
  Object.freeze({ id: 'mercury', sanskrit: 'Budha Rekhā', english: 'Mercury line', max: 1 }),
  Object.freeze({ id: 'marriage', sanskrit: 'Vivāha Rekhā', english: 'Marriage lines', max: 3 })
]);

export const LOW_CONFIDENCE = 0.5;
export const MIN_SCORE = 0.25;

const g = (x, mu, sigma) => Math.exp(-0.5 * ((x - mu) / sigma) ** 2);
const ramp = (x, a, b) => Math.max(0, Math.min(1, (x - a) / (b - a)));

/** Convert an image-space polyline to palm space and compute the features the templates use. */
export function palmFeatures(polyline, frame) {
  const pts = (polyline.points ?? polyline).map((p) => { const q = toPalmSpace(p, frame); return [q.u, q.v]; });
  const us = pts.map((p) => p[0]); const vs = pts.map((p) => p[1]);
  const uMin = Math.min(...us); const uMax = Math.max(...us); const vMin = Math.min(...vs); const vMax = Math.max(...vs);
  const mean = (a) => a.reduce((x, y) => x + y, 0) / a.length;
  const a = pts[0]; const b = pts[pts.length - 1];
  const len = lengthOf(simplify(pts, 0.01));
  // end nearer the ulnar edge / nearer the wrist, whichever is meaningful for each family
  const ulnarEnd = a[0] >= b[0] ? a : b; const thumbEnd = a[0] >= b[0] ? b : a;
  const lowEnd = a[1] <= b[1] ? a : b; const highEnd = a[1] <= b[1] ? b : a;
  return {
    pts, uMin, uMax, vMin, vMax, du: uMax - uMin, dv: vMax - vMin, uMean: mean(us), vMean: mean(vs),
    length: len, ulnarEnd, thumbEnd, lowEnd, highEnd
  };
}

const TEMPLATES = {
  heart: (f) => ramp(f.du, 0.25, 0.5) * ramp(f.du / Math.max(f.dv, 1e-6), 1.2, 2.5) * g(f.vMean, 0.8, 0.12) * g(f.ulnarEnd[0], 1.0, 0.18) * ramp(f.length, 0.3, 0.55),
  head: (f) => ramp(f.du, 0.25, 0.5) * ramp(f.du / Math.max(f.dv, 1e-6), 1.2, 2.5) * g(f.vMean, 0.5, 0.12) * g(f.thumbEnd[0], 0.12, 0.18) * ramp(f.length, 0.3, 0.55),
  life: (f) => ramp(f.dv, 0.3, 0.5) * g(f.uMean, 0.25, 0.14) * g(f.lowEnd[1], 0.05, 0.14) * g(f.highEnd[1], 0.65, 0.2) * ramp(f.length, 0.35, 0.6) * ramp(f.dv / Math.max(f.du, 1e-6), 1.2, 2.2),
  fate: (f) => ramp(f.dv, 0.3, 0.5) * ramp(f.dv / Math.max(f.du, 1e-6), 2, 4) * g(f.uMean, 0.5, 0.12) * g(f.lowEnd[1], 0.1, 0.2) * ramp(f.length, 0.3, 0.5),
  sun: (f) => ramp(f.dv, 0.2, 0.4) * ramp(f.dv / Math.max(f.du, 1e-6), 2, 4) * g(f.uMean, 0.7, 0.09) * g(f.lowEnd[1], 0.4, 0.2) * g(f.highEnd[1], 0.75, 0.2),
  mercury: (f) => ramp(f.dv, 0.2, 0.4) * ramp(f.dv / Math.max(f.du, 1e-6), 1, 2.5) * g(f.uMean, 0.83, 0.09) * g(f.lowEnd[1], 0.3, 0.2) * g(f.highEnd[1], 0.72, 0.2),
  marriage: (f) => ramp(f.du, 0.04, 0.12) * ramp(f.du / Math.max(f.dv, 1e-6), 1.2, 3) * g(f.vMean, 0.83, 0.07) * g(f.uMax, 1.0, 0.1) * (f.length < 0.3 ? 1 : ramp(0.45 - f.length, 0, 0.15))
};

/**
 * Score every polyline against every line type.
 * Returns `[ { index, scores: { heart: 0.9, … }, features } ]`.
 */
export function scoreAll(polylines, frame) {
  return polylines.map((pl, index) => {
    const features = palmFeatures(pl, frame);
    const scores = {};
    for (const t of LINE_TYPES) scores[t.id] = Math.max(0, Math.min(1, TEMPLATES[t.id](features)));
    return { index, scores, features };
  });
}

/**
 * Confidence of an assignment, 0–1: the template fit, discounted when a second
 * type fits nearly as well (an ambiguous curve is not a confident one).
 */
export function confidenceOf(assignment) {
  const { score, runnerUp } = assignment;
  const margin = score > 0 ? (score - (runnerUp?.score ?? 0)) / score : 0;
  return Math.max(0, Math.min(1, score * (0.4 + 0.6 * margin)));
}

/**
 * Assign polylines to line types. A polyline gets at most one type; each type at
 * most `max` polylines (marriage lines may be several). Greedy by score.
 *
 * Returns `{ assignments, unclassified }`. An assignment is
 * `{ index, type, score, confidence, low, runnerUp: { type, score } | null, features }`.
 */
export function classify(polylines, frame, { minScore = MIN_SCORE, lowBelow = LOW_CONFIDENCE } = {}) {
  const all = scoreAll(polylines, frame);
  const pairs = [];
  for (const r of all) for (const t of LINE_TYPES) if (r.scores[t.id] >= minScore) pairs.push({ index: r.index, type: t.id, score: r.scores[t.id] });
  pairs.sort((a, b) => b.score - a.score);
  const usedPoly = new Set(); const count = {}; const assignments = [];
  for (const p of pairs) {
    if (usedPoly.has(p.index)) continue;
    const limit = LINE_TYPES.find((t) => t.id === p.type).max;
    if ((count[p.type] ?? 0) >= limit) continue;
    usedPoly.add(p.index); count[p.type] = (count[p.type] ?? 0) + 1;
    const r = all[p.index];
    const others = LINE_TYPES.filter((t) => t.id !== p.type).map((t) => ({ type: t.id, score: r.scores[t.id] })).sort((a, b) => b.score - a.score);
    const a = { index: p.index, type: p.type, score: p.score, runnerUp: others[0].score >= minScore ? others[0] : null, features: r.features };
    a.confidence = confidenceOf(a); a.low = a.confidence < lowBelow;
    assignments.push(a);
  }
  const unclassified = all.filter((r) => !usedPoly.has(r.index)).map((r) => r.index);
  return { assignments, unclassified };
}

/** Manual correction: the user says this polyline is `type`. Confidence becomes 1 — a person decided. */
export function reclassify(assignments, index, type) {
  const out = assignments.filter((a) => a.index !== index && !(type !== 'marriage' && a.type === type));
  if (type) out.push({ index, type, score: 1, confidence: 1, low: false, runnerUp: null, manual: true });
  return out;
}

/** Lines the classifier did not find — reported as absent, never silently omitted. */
export function absentLines(assignments) {
  const have = new Set(assignments.map((a) => a.type));
  return LINE_TYPES.filter((t) => !have.has(t.id)).map((t) => t.id);
}

/**
 * Re-measure assignments after the user has moved points on the polylines.
 * A manual label keeps confidence 1 (a person decided); an automatic one is
 * re-scored against its type, so dragging a heart line somewhere implausible
 * visibly lowers its confidence instead of leaving a stale number.
 */
export function refit(assignments, polylines, frame, { lowBelow = LOW_CONFIDENCE } = {}) {
  const all = scoreAll(polylines, frame);
  return assignments.filter((a) => polylines[a.index]).map((a) => {
    const r = all[a.index];
    if (a.manual) return { ...a, features: r.features };
    const others = LINE_TYPES.filter((t) => t.id !== a.type).map((t) => ({ type: t.id, score: r.scores[t.id] })).sort((x, y) => y.score - x.score);
    const next = { ...a, score: r.scores[a.type], runnerUp: others[0].score >= MIN_SCORE ? others[0] : null, features: r.features };
    next.confidence = confidenceOf(next); next.low = next.confidence < lowBelow;
    return next;
  });
}
